import React from 'react';
import cn from 'classnames';
import { XIcon } from '@phosphor-icons/react';

import { MPFIconButton } from 'volto-ministerio-publico-federal/components/UI/IconButton';

export type MPFModalSize = 'sm' | 'md' | 'lg';

export interface MPFModalProps {
  /** Estado controlado pelo consumidor — o `<dialog>` é sincronizado por efeito. */
  open: boolean;
  /**
   * Chamado em toda saída: botão X, clique no backdrop e Esc (que fecha o
   * dialog nativamente, sem passar pelos handlers acima).
   */
  onClose: () => void;
  title: string;
  /** Largura máxima em telas `sm+`. `md` (842px) é a medida do design. */
  size?: MPFModalSize;
  /**
   * `false` remove o X e trava backdrop/Esc — para fluxos que não podem ser
   * abandonados no meio (ex.: envio em andamento).
   */
  dismissible?: boolean;
  /** Faixa fixa no rodapé, fora da área rolável. */
  footer?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

const SIZE_CLASS: Record<MPFModalSize, string> = {
  sm: 'sm:max-w-[480px]',
  md: 'sm:max-w-[842px]',
  lg: 'sm:max-w-[1080px]',
};

/**
 * Modal genérico do kit de UI.
 *
 * Usa `<dialog>` nativo com `showModal()`, no mesmo espírito do
 * `YoutubeDialog.tsx` e do `MobileSidebarSheet`: trava de foco, Esc-to-close,
 * bloqueio de scroll do fundo e `::backdrop` vêm do navegador, sem
 * reimplementar nada disso em JS nem trazer biblioteca de portal.
 *
 * O elemento nasce sempre no DOM (fechado, já oculto pelo navegador), então
 * `open` é só um espelho — não há montagem/desmontagem condicional, e o
 * conteúdo mantém estado entre aberturas se o consumidor quiser.
 */
export const MPFModal: React.FC<MPFModalProps> = ({
  open,
  onClose,
  title,
  size = 'md',
  dismissible = true,
  footer,
  className,
  children,
}) => {
  const ref = React.useRef<HTMLDialogElement>(null);
  const titleId = React.useId();

  // Espelha `open` no elemento. `showModal()` estoura se já estiver aberto,
  // e `close()` dispara o evento `close` — por isso os guardas em `dialog.open`.
  React.useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open && typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Cobre o Esc, que fecha o dialog nativamente sem passar por `onClose`.
  React.useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    const handleClose = () => onClose();
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  // `cancel` é o evento do Esc *antes* do fechamento — é onde dá para vetá-lo
  // quando o modal não é dispensável.
  React.useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    const handleCancel = (event: Event) => {
      if (!dismissible) event.preventDefault();
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [dismissible]);

  // Clique no fundo. Ouvinte imperativo (não `onClick` no JSX): o alvo do
  // clique no backdrop é o próprio `<dialog>`.
  React.useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !dismissible) return undefined;
    const handleBackdropClick = (event: MouseEvent) => {
      if (event.target === dialog) onClose();
    };
    dialog.addEventListener('click', handleBackdropClick);
    return () => dialog.removeEventListener('click', handleBackdropClick);
  }, [dismissible, onClose]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className={cn('mpf-dialog', SIZE_CLASS[size], className)}
    >
      <div className="mpf-dialog__panel">
        <header className="mpf-dialog__header">
          <h2 id={titleId} className="mpf-dialog__title">
            {title}
          </h2>
          <span aria-hidden="true" className="mpf-dialog__rule" />
          {dismissible && (
            <MPFIconButton
              icon={XIcon}
              label="Fechar"
              variant="ghost"
              size="m"
              shape="square"
              className="mpf-dialog__close"
              onClick={onClose}
            />
          )}
        </header>

        <div className="mpf-dialog__body">{children}</div>

        {footer && <footer className="mpf-dialog__actions">{footer}</footer>}
      </div>
    </dialog>
  );
};

export default MPFModal;
